/**
 * Table displaying vault invitations with management actions
 */

import { useState } from "react";
import {
  useResendInvitation,
  useRevokeInvitation,
  useVaultInvitations,
} from "../../hooks/useInvitations";
import LoadingSpinner from "../Common/LoadingSpinner";

interface InvitationsTableProps {
  vaultId: number;
}

export default function InvitationsTable({ vaultId }: InvitationsTableProps) {
  const { data: invitations = [], isLoading } = useVaultInvitations(vaultId);
  const revokeInvitationMutation = useRevokeInvitation(vaultId);
  const resendInvitationMutation = useResendInvitation(vaultId);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const handleCopyLink = async (link: string, invitationId: number) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(invitationId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleRevoke = async (invitationId: number, email: string) => {
    if (confirm(`Are you sure you want to revoke the invitation for ${email}?`)) {
      await revokeInvitationMutation.mutateAsync(invitationId);
    }
  };

  const handleResend = async (invitationId: number, email: string) => {
    if (confirm(`Resend invitation to ${email}? This will generate a new link and invalidate the old one.`)) {
      const result = await resendInvitationMutation.mutateAsync(invitationId);
      await handleCopyLink(result.invitation_link, invitationId);
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-block rounded-full px-2 py-1 text-xs font-medium";
    switch (status) {
      case "pending":
        return (
          <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>
            Pending
          </span>
        );
      case "accepted":
        return (
          <span className={`${baseClasses} bg-green-100 text-green-800`}>
            Accepted
          </span>
        );
      case "expired":
        return (
          <span className={`${baseClasses} bg-gray-100 text-gray-800`}>
            Expired
          </span>
        );
      case "revoked":
        return (
          <span className={`${baseClasses} bg-red-100 text-red-800`}>
            Revoked
          </span>
        );
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className="text-center py-8 text-vault-500">
        No invitations found. Create an invitation to invite members to this vault.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-vault-200 text-left text-sm text-vault-600">
            <th className="pb-3 font-medium">Email</th>
            <th className="pb-3 font-medium">Role</th>
            <th className="pb-3 font-medium">Status</th>
            <th className="pb-3 font-medium">Expires</th>
            <th className="pb-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((invitation, idx) => (
            <tr
              key={invitation.id}
              className={`text-sm ${
                idx % 2 === 0 ? "bg-vault-50" : "bg-white"
              } hover:bg-vault-100 transition-colors`}
            >
              <td className="py-3 px-2">{invitation.invited_email}</td>
              <td className="py-3 px-2">
                <span
                  className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${
                    invitation.invited_role === "VAULT_CO_ADMIN"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {invitation.invited_role === "VAULT_CO_ADMIN"
                    ? "Co-Admin"
                    : "Member"}
                </span>
              </td>
              <td className="py-3 px-2">{getStatusBadge(invitation.status)}</td>
              <td className="py-3 px-2 text-vault-600">
                {new Date(invitation.expires_at).toLocaleDateString()}
              </td>
              <td className="py-3 px-2">
                <div className="flex gap-2">
                  {invitation.status === "pending" && (
                    <>
                      <button
                        onClick={() =>
                          handleCopyLink(invitation.invitation_link, invitation.id)
                        }
                        className="text-vault-600 hover:text-vault-900 text-xs underline"
                        title="Copy invitation link"
                      >
                        {copiedId === invitation.id ? "Copied!" : "Copy Link"}
                      </button>
                      <button
                        onClick={() => handleResend(invitation.id, invitation.invited_email)}
                        disabled={resendInvitationMutation.isPending}
                        className="text-blue-600 hover:text-blue-800 text-xs underline disabled:opacity-50"
                        title="Resend invitation"
                      >
                        Resend
                      </button>
                      <button
                        onClick={() => handleRevoke(invitation.id, invitation.invited_email)}
                        disabled={revokeInvitationMutation.isPending}
                        className="text-red-600 hover:text-red-800 text-xs underline disabled:opacity-50"
                        title="Revoke invitation"
                      >
                        Revoke
                      </button>
                    </>
                  )}
                  {invitation.status === "accepted" && (
                    <span className="text-xs text-vault-500">
                      Accepted {new Date(invitation.accepted_at!).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
